// ============================================
// APPLICATION - Almacen Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { almacenRepository } from '../../infrastructure/http/almacen.repository';

/**
 * Hook para obtener sucursales por RUT (público, no requiere auth)
 */
export const useGetSucursalesByRut = (rutAlmacen: string | null) => {
  return useQuery({
    queryKey: ['almacenes', 'sucursales', rutAlmacen],
    queryFn: () => almacenRepository.getSucursalesByRut(rutAlmacen!),
    enabled: !!rutAlmacen && rutAlmacen.length > 0,
    retry: false,
    staleTime: 0,
  });
};

/**
 * Hook para obtener usuarios disponibles para login en una sucursal
 */
export const useGetUsuariosLogin = (almacenId: number | null) => {
  return useQuery({
    queryKey: ['almacenes', almacenId, 'usuarios-login'],
    queryFn: () => almacenRepository.getUsuariosLogin(almacenId!),
    enabled: !!almacenId,
    retry: false,
    staleTime: 0,
  });
};

/**
 * Hook para obtener el perfil del almacén del usuario autenticado
 */
export const usePerfilAlmacen = () => {
  return useQuery({
    queryKey: ['almacenes', 'perfil'],
    queryFn: () => almacenRepository.getPerfilAlmacen(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para obtener el logo del almacén del usuario autenticado
 */
export const useLogoAlmacen = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['almacenes', 'logo'],
    queryFn: () => almacenRepository.getLogoAlmacen(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para eliminar el logo del almacén
 */
export const useEliminarLogoAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => almacenRepository.eliminarLogoAlmacen(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['almacenes', 'logo'] });
    },
  });
};

/**
 * Hook para subir el logo del almacén
 */
export const useSubirLogoAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, colorFondo }: { file: File; colorFondo: string }) =>
      almacenRepository.subirLogoAlmacen(file, colorFondo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['almacenes', 'logo'] });
    },
  });
};
